import { createContext, type ReactNode, type Ref, useContext } from "react";
import {
	Radio as AriaRadio,
	RadioGroup as AriaRadioGroup,
	type RadioGroupProps as AriaRadioGroupProps,
	type RadioProps as AriaRadioProps,
} from "react-aria-components";

import { cx } from "@/utils/cx";

export interface RadioGroupContextType {
	size?: "md" | "sm";
}

const RadioGroupContext = createContext<null | RadioGroupContextType>(null);

export interface RadioButtonBaseProps {
	className?: string;
	isDisabled?: boolean;
	isFocusVisible?: boolean;
	isSelected?: boolean;
	size?: "md" | "sm";
}

export const RadioButtonBase = ({
	className,
	isDisabled,
	isFocusVisible,
	isSelected,
	size = "sm",
}: RadioButtonBaseProps) => {
	return (
		<div
			className={cx(
				"flex size-4 min-h-4 min-w-4 cursor-pointer appearance-none items-center justify-center rounded-full bg-primary ring-1 ring-primary ring-inset",
				size === "md" && "size-5 min-h-5 min-w-5",
				isSelected && !isDisabled && "bg-brand-solid ring-brand-solid",
				isDisabled && "cursor-not-allowed opacity-50",
				isFocusVisible && "outline-2 outline-offset-2 outline-focus-ring",
				className,
			)}
		>
			<div
				className={cx(
					"size-1.5 rounded-full bg-fg-white opacity-0 transition-inherit-all",
					size === "md" && "size-2",
					isDisabled && "bg-fg-disabled_subtle",
					isSelected && "opacity-100",
				)}
			/>
		</div>
	);
};
RadioButtonBase.displayName = "RadioButtonBase";

interface RadioButtonProps extends AriaRadioProps {
	hint?: ReactNode;
	label?: ReactNode;
	ref?: Ref<HTMLLabelElement>;
	size?: "md" | "sm";
}

export const RadioButton = ({
	className,
	hint,
	label,
	size = "sm",
	...ariaRadioProps
}: RadioButtonProps) => {
	const context = useContext(RadioGroupContext);

	size = context?.size ?? size;

	const sizes = {
		md: {
			hint: "text-md",
			label: "text-md font-medium",
			root: "gap-3",
			textWrapper: "gap-0.5",
		},
		sm: {
			hint: "text-sm",
			label: "text-sm font-medium",
			root: "gap-2",
			textWrapper: "",
		},
	};

	return (
		<AriaRadio
			{...ariaRadioProps}
			className={(renderProps) =>
				cx(
					"flex items-start",
					renderProps.isDisabled && "cursor-not-allowed",
					sizes[size].root,
					typeof className === "function" ? className(renderProps) : className,
				)
			}
		>
			{({ isDisabled, isFocusVisible, isSelected }) => (
				<>
					<RadioButtonBase
						className={label || hint ? "mt-0.5" : ""}
						isDisabled={isDisabled}
						isFocusVisible={isFocusVisible}
						isSelected={isSelected}
						size={size}
					/>
					{(label || hint) && (
						<div
							className={cx("inline-flex flex-col", sizes[size].textWrapper)}
						>
							{label && (
								<p
									className={cx(
										"text-secondary select-none",
										sizes[size].label,
									)}
								>
									{label}
								</p>
							)}
							{hint && (
								<span
									className={cx("text-tertiary", sizes[size].hint)}
									onClick={(event) => event.stopPropagation()}
								>
									{hint}
								</span>
							)}
						</div>
					)}
				</>
			)}
		</AriaRadio>
	);
};
RadioButton.displayName = "RadioButton";

interface RadioGroupProps extends AriaRadioGroupProps, RadioGroupContextType {
	children: ReactNode;
	className?: string;
}

export const RadioGroup = ({
	children,
	className,
	size = "sm",
	...props
}: RadioGroupProps) => {
	return (
		<RadioGroupContext.Provider value={{ size }}>
			<AriaRadioGroup
				{...props}
				className={cx("flex flex-col gap-4", className)}
			>
				{children}
			</AriaRadioGroup>
		</RadioGroupContext.Provider>
	);
};
